import { Injectable } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import {
  ClaimsState,
  IAsyncData,
  IClaimsBatchStatisticsState,
  IClaimsData,
  IClaimsState,
  IData,
  IDataNonPaginated,
  IPendingState
} from '@module-claims/store/claims.state';
import { Observable, Subject, of } from 'rxjs';
import { Customer } from '@module-customer/models/model-customer';
import * as actions from '@module-claims/store/claims.actions';
import * as pharmacyActions from '@module-claims/store/pharmacy.actions';
import * as prescriberActions from '@module-claims/store/prescriber.actions';
import * as claimCEActions from '@module-claims/store/claim-ce.actions';
import * as mfpClaimsActions from '@module-claims/store/mfp-claims.actions';
import * as auditClaimSearchActions from '@module-claims/store/audit-claim-search.actions';
import {
  IClaimUpload,
  IClaimsFileUploadLogParams
} from '@module-claims/models/claim-upload.interface';
import {
  IClaimFile340BAudit,
  IClaimTotals340BAudit,
  IClaimsGroupClaimV2,
  IClaimsGroupSummaryItemV2
} from '@module-claims/models/claims-group.interface';
import {
  IClaimAuditStatusUpdateParams,
  IClaimDetail
} from '@module-claims/models/claim.interface';
import {
  CompositeFilterDescriptor,
  SortDescriptor
} from '@progress/kendo-data-query';
import { ModelUser } from '@module-user/models/model-user';
import { IClaimListElement } from '@module-claims/models/claim-list-element.interface';
import { map, switchMap, tap } from 'rxjs/operators';
import { IClaimsSummary } from '@module-claims/models/claims-summary.interface';
import { ClaimFileDownloadRequest } from '@module-claims/models/claim-file-download.interface';
import { CustomerServiceType } from '@module-customer/enums/customer-service-type.enum';
import { IClaimFileStatus } from '@module-claims/models/claim-file-status.interface';
import { BatchStatisticsLevel } from '@module-claims/enums/batch-statistics-level.enum';
import {
  ClaimsSummaryParams,
  ClaimsSummaryStatisticsParams,
  ClaimsGroupListParamsV2,
  ClaimBatchUpdateParamsV2,
  AuditClaimFileSummaryParams,
  AuditReportingStatusTotals,
  AuditReportingResolutionTrend,
  AuditReportingTop340BMetrics,
  AuditReportingTopNot340BMetrics,
  AuditReportingTopDisputedMetrics,
  AuditReportingTopDisputeResolutionMetrics,
  AuditClaimSearchParams,
  AuditClaimSearchResult
} from '@module-claims/models/claims-http.interface';
import { PharmacyBenefitManager } from '@module-claims/models/pharmacy-benefit-manager.interface';
import { IClaimFileLOB } from '@module-claims/models/claim-file-lob.interface';
import {
  PharmacyDetails,
  ContractPharmacyDetails,
  InhousePharmacyDetails,
  ContractPharmacyMappingDetails,
  InhousePharmacyMappingDetails,
  ContractPharmacyLinkParams,
  InhousePharmacyLinkParams
} from '@module-claims/models/pharmacy.interface';
import {
  PrescriberCEAssociationAddParams,
  PrescriberCEAssociationRemoveParams,
  PrescriberDetailsSimple
} from '@module-claims/models/prescriber.interface';
import {
  ClaimsCEsContactParams,
  ClaimsCEsGetParams,
  ClaimsCEsGetResponse
} from '@module-claims/models/claim-ce.interface';
import {
  MFPClaim,
  MFPClaimsGetParams,
  MFPClaimsUpdateParams
} from '@module-claims/models/mfp-claim.interface';

@Injectable()
export class ClaimsService {
  @Select(ClaimsState.customersData)
  customersData$!: Observable<IClaimsData<Customer[]>>;

  @Select(ClaimsState.pbmData)
  pbmData$!: Observable<IDataNonPaginated<PharmacyBenefitManager[]>>;

  @Select(ClaimsState.lobData)
  lobData$!: Observable<IDataNonPaginated<IClaimFileLOB[]>>;

  @Select(ClaimsState.pharmacyDetails)
  pharmacyDetails$!: Observable<IAsyncData<PharmacyDetails>>;

  @Select(ClaimsState.contractPharmacyDetails)
  contractPharmacyDetails$!: Observable<IAsyncData<ContractPharmacyDetails>>;

  @Select(ClaimsState.inhousePharmacyDetails)
  inhousePharmacyDetails$!: Observable<IAsyncData<InhousePharmacyDetails>>;

  @Select(ClaimsState.prescriberDetails)
  prescriberDetails$!: Observable<IAsyncData<PrescriberDetailsSimple>>;

  @Select(ClaimsState.auditClaimSearch)
  auditClaimSearch$!: Observable<IAsyncData<AuditClaimSearchResult>>;

  @Select(ClaimsState.claimsUploadData)
  claimsUploadData$!: Observable<IData<IClaimUpload[]>>;

  @Select(ClaimsState.claimFileDownload)
  claimFileDownload$!: Observable<IPendingState>;

  @Select(ClaimsState.claimFileDelete)
  claimFileDelete$!: Observable<IPendingState>;

  @Select(ClaimsState.claimFileStatus)
  claimFileStatus$!: Observable<
    IDataNonPaginated<IClaimFileStatus | undefined>
  >;

  @Select(ClaimsState.claimsGroupsDataV2)
  claimsGroupsDataV2$!: Observable<IData<IClaimsGroupSummaryItemV2[]>>;

  @Select(ClaimsState.claimsGroupClaimsDataV2)
  claimsGroupClaimsDataV2$!: Observable<IData<IClaimsGroupClaimV2[]>>;

  @Select(ClaimsState.claimsData)
  claimsData$!: Observable<IClaimsData<IClaimListElement[]>>;

  @Select(ClaimsState.claimsCustomersData)
  claimsCustomersData$!: Observable<IData<Customer[]>>;

  @Select(ClaimsState.claimDetailsData)
  claimDetailsData$!: Observable<IDataNonPaginated<IClaimDetail | undefined>>;

  @Select(ClaimsState.claimUpdateData)
  claimUpdateData$!: Observable<IDataNonPaginated<any>>;

  @Select(ClaimsState.auditClaimUpdate)
  auditClaimUpdate$!: Observable<IPendingState>;

  @Select(ClaimsState.auditClaimManualReviewComplete)
  auditClaimManualReviewComplete$!: Observable<IPendingState>;

  @Select(ClaimsState.claimsSummaryData)
  claimsSummaryData$!: Observable<IData<IClaimsSummary[]>>;

  @Select(ClaimsState.auditClaimFileData)
  auditClaimFileData$!: Observable<IDataNonPaginated<IClaimFile340BAudit[]>>;

  @Select(ClaimsState.auditReviewedClaimFileData)
  auditReviewedClaimFileData$!: Observable<
    IDataNonPaginated<IClaimFile340BAudit[]>
  >;

  @Select(ClaimsState.auditClaimTotalsData)
  auditClaimTotalsData$!: Observable<
    IDataNonPaginated<IClaimTotals340BAudit | undefined>
  >;

  @Select(ClaimsState.auditReportingStatusTotals)
  auditReportingStatusTotals$!: Observable<
    IDataNonPaginated<AuditReportingStatusTotals | undefined>
  >;

  @Select(ClaimsState.auditReportingResolutionTrends)
  auditReportingResolutionTrends$!: Observable<
    IDataNonPaginated<AuditReportingResolutionTrend[]>
  >;

  @Select(ClaimsState.auditReportingTop340B)
  auditReportingTop340B$!: Observable<
    IDataNonPaginated<AuditReportingTop340BMetrics | undefined>
  >;

  @Select(ClaimsState.auditReportingTopNot340B)
  auditReportingTopNot340B$!: Observable<
    IDataNonPaginated<AuditReportingTopNot340BMetrics | undefined>
  >;

  @Select(ClaimsState.auditReportingTopDisputed)
  auditReportingTopDisputed$!: Observable<
    IDataNonPaginated<AuditReportingTopDisputedMetrics | undefined>
  >;

  @Select(ClaimsState.auditReportingTopDisputeResolution)
  auditReportingTopDisputeResolution$!: Observable<
    IDataNonPaginated<AuditReportingTopDisputeResolutionMetrics | undefined>
  >;

  @Select(
    ClaimsState.claimsSummaryBatchStatisticsData(
      BatchStatisticsLevel.MFG_APPROVED
    )
  )
  claimsSummaryBatchStatisticsMfgApprovedData$!: Observable<IClaimsBatchStatisticsState>;

  @Select(
    ClaimsState.claimsSummaryBatchStatisticsData(
      BatchStatisticsLevel.RXP_APPROVED
    )
  )
  claimsSummaryBatchStatisticsRxpApprovedData$!: Observable<IClaimsBatchStatisticsState>;

  @Select(
    ClaimsState.claimsSummaryBatchStatisticsData(
      BatchStatisticsLevel.RXP_APPROVED_MFG_REVIEW
    )
  )
  claimsSummaryBatchStatisticsRxpApprovedMfgReviewData$!: Observable<IClaimsBatchStatisticsState>;

  @Select(
    ClaimsState.claimsSummaryBatchStatisticsData(
      BatchStatisticsLevel.REJECTED_REBATEABLE_RXP_REVIEW
    )
  )
  claimsSummaryBatchStatisticsRejectedRebateableRxpReviewData$!: Observable<IClaimsBatchStatisticsState>;

  @Select(
    ClaimsState.claimsSummaryBatchStatisticsData(
      BatchStatisticsLevel.REJECTED_REBATEABLE_CUSTOMER_REVIEW
    )
  )
  claimsSummaryBatchStatisticsRejectedRebateableCustomerReviewData$!: Observable<IClaimsBatchStatisticsState>;

  @Select(
    ClaimsState.claimsSummaryBatchStatisticsData(
      BatchStatisticsLevel.REJECTED_CUSTOMER_APPROVED
    )
  )
  claimsSummaryBatchStatisticsRejectedCustomerApprovedData$!: Observable<IClaimsBatchStatisticsState>;

  @Select(
    ClaimsState.claimsSummaryBatchStatisticsData(
      BatchStatisticsLevel.MFG_REJECTED
    )
  )
  claimsSummaryBatchStatisticsMfgRejectedData$!: Observable<IClaimsBatchStatisticsState>;

  @Select(
    ClaimsState.claimsSummaryBatchStatisticsData(
      BatchStatisticsLevel.CUSTOMER_RESUBMISSION
    )
  )
  claimsSummaryBatchStatisticsCustomerResubmissionData$!: Observable<IClaimsBatchStatisticsState>;

  @Select(
    ClaimsState.claimsSummaryBatchStatisticsData(
      BatchStatisticsLevel.CUSTOMER_RESUBMISSION_REJECTED
    )
  )
  claimsSummaryBatchStatisticsCustomerResubmissionRejectedData$!: Observable<IClaimsBatchStatisticsState>;

  @Select(
    ClaimsState.claimsSummaryBatchStatisticsData(
      BatchStatisticsLevel.AUDIT_340B_BY_DRUG
    )
  )
  claimsSummaryBatchStatisticsAudit340bByDrugData$!: Observable<IClaimsBatchStatisticsState>;

  @Select(
    ClaimsState.claimsSummaryBatchStatisticsData(
      BatchStatisticsLevel.AUDIT_340B_BY_PHARMACY
    )
  )
  claimsSummaryBatchStatisticsAudit340bByPharmacyData$!: Observable<IClaimsBatchStatisticsState>;

  @Select(
    ClaimsState.claimsSummaryBatchStatisticsData(
      BatchStatisticsLevel.AUDIT_340B_BY_PRESCRIBER
    )
  )
  claimsSummaryBatchStatisticsAudit340bByPrescriberData$!: Observable<IClaimsBatchStatisticsState>;

  @Select(ClaimsState.auditClaimsCEs)
  auditClaimsCEs$!: Observable<IAsyncData<ClaimsCEsGetResponse>>;

  @Select(ClaimsState.auditClaimsCEsContact)
  auditClaimsCEsContact$!: Observable<IPendingState>;

  @Select(ClaimsState.mfpClaims)
  mfpClaims$!: Observable<IData<MFPClaim[]>>;

  @Select(ClaimsState.mfpClaimsUpdate)
  mfpClaimsUpdate$!: Observable<IPendingState>;

  claimUpdated$ = new Subject<string | undefined>();

  constructor(private _store: Store) {}

  getCustomers(filters?: CompositeFilterDescriptor): Observable<ClaimsState> {
    return this._store
      .dispatch(new actions.GetCustomersList(filters))
      .pipe(map(() => this._store.snapshot()));
  }

  getPBMList(): Observable<{ Claims: IClaimsState }> {
    return this._store
      .dispatch(new actions.GetPBMList())
      .pipe(map(() => this._store.snapshot()));
  }

  getLOBList(): Observable<{ Claims: IClaimsState }> {
    return this._store
      .dispatch(new actions.GetClaimFileLOBList())
      .pipe(map(() => this._store.snapshot()));
  }

  clearPharmacyDetails(): Observable<{ Claims: IClaimsState }> {
    return this._store
      .dispatch(new pharmacyActions.ClearPharmacyDetails())
      .pipe(map(() => this._store.snapshot()));
  }

  getPharmacyDetails(npi: string): Observable<{ Claims: IClaimsState }> {
    return this._store
      .dispatch(new pharmacyActions.GetPharmacyDetails(npi))
      .pipe(map(() => this._store.snapshot()));
  }

  clearContractPharmacyDetails(): Observable<{ Claims: IClaimsState }> {
    return this._store
      .dispatch(new pharmacyActions.ClearContractPharmacyDetails())
      .pipe(map(() => this._store.snapshot()));
  }

  getContractPharmacyDetails(
    hrsaPharmacyId: string
  ): Observable<{ Claims: IClaimsState }> {
    return this._store
      .dispatch(new pharmacyActions.GetContractPharmacyDetails(hrsaPharmacyId))
      .pipe(map(() => this._store.snapshot()));
  }

  linkContractPharmacy(
    params: ContractPharmacyLinkParams
  ): Observable<{ Claims: IClaimsState }> {
    return this._store
      .dispatch(new pharmacyActions.LinkContractPharmacy(params))
      .pipe(map(() => this._store.snapshot()));
  }

  unlinkContractPharmacy(
    npi: string,
    contractPharmacy: ContractPharmacyMappingDetails
  ): Observable<{ Claims: IClaimsState }> {
    return this._store
      .dispatch(
        new pharmacyActions.UnlinkContractPharmacy(npi, contractPharmacy)
      )
      .pipe(map(() => this._store.snapshot()));
  }

  clearInhousePharmacyDetails(): Observable<{ Claims: IClaimsState }> {
    return this._store
      .dispatch(new pharmacyActions.ClearInhousePharmacyDetails())
      .pipe(map(() => this._store.snapshot()));
  }

  getInhousePharmacyDetails(
    id340b: string
  ): Observable<{ Claims: IClaimsState }> {
    return this._store
      .dispatch(new pharmacyActions.GetInhousePharmacyDetails(id340b))
      .pipe(map(() => this._store.snapshot()));
  }

  linkInhousePharmacy(
    params: InhousePharmacyLinkParams
  ): Observable<{ Claims: IClaimsState }> {
    return this._store
      .dispatch(new pharmacyActions.LinkInhousePharmacy(params))
      .pipe(map(() => this._store.snapshot()));
  }

  unlinkInhousePharmacy(
    npi: string,
    inhousePharmacy: InhousePharmacyMappingDetails
  ): Observable<{ Claims: IClaimsState }> {
    return this._store
      .dispatch(new pharmacyActions.UnlinkInhousePharmacy(npi, inhousePharmacy))
      .pipe(map(() => this._store.snapshot()));
  }

  clearPrescriberDetails(): Observable<{ Claims: IClaimsState }> {
    return this._store
      .dispatch(new prescriberActions.ClearPrescriberDetails())
      .pipe(map(() => this._store.snapshot()));
  }

  getPrescriberDetails(npi: string): Observable<{ Claims: IClaimsState }> {
    return this._store
      .dispatch(new prescriberActions.GetPrescriberDetails(npi))
      .pipe(map(() => this._store.snapshot()));
  }

  addPrescriberCEAssociation(
    params: PrescriberCEAssociationAddParams
  ): Observable<{ Claims: IClaimsState }> {
    return this._store
      .dispatch(new prescriberActions.AddPresciberCEAssociation(params))
      .pipe(map(() => this._store.snapshot()));
  }

  removePrescriberCEAssociation(
    params: PrescriberCEAssociationRemoveParams
  ): Observable<{ Claims: IClaimsState }> {
    return this._store
      .dispatch(new prescriberActions.RemovePresciberCEAssociation(params))
      .pipe(map(() => this._store.snapshot()));
  }

  clearAuditClaimSearch(): Observable<{ Claims: IClaimsState }> {
    return this._store
      .dispatch(new auditClaimSearchActions.ActionAuditClaimSearchClear())
      .pipe(map(() => this._store.snapshot()));
  }

  searchAuditClaim(
    params: AuditClaimSearchParams
  ): Observable<{ Claims: IClaimsState }> {
    return this._store
      .dispatch(
        new auditClaimSearchActions.ActionAuditClaimSearchSubmit(params)
      )
      .pipe(map(() => this._store.snapshot()));
  }

  getCustomersAsCustomers(
    filters?: CompositeFilterDescriptor
  ): Observable<IData<Customer[]>> {
    return this._store
      .dispatch(new actions.GetCustomersList(filters))
      .pipe(map(() => this._store.snapshot()));
  }

  getClaimsUploadList(
    params: IClaimsFileUploadLogParams
  ): Observable<{ Claims: IClaimsState }> {
    return this._store
      .dispatch(new actions.GetClaimsUploadList(params))
      .pipe(map(() => this._store.snapshot()));
  }

  downloadClaimFile(params: ClaimFileDownloadRequest): Observable<ClaimsState> {
    return this._store
      .dispatch(new actions.DownloadClaimFile(params))
      .pipe(map(() => this._store.snapshot()));
  }

  deleteClaimFile(fileId: string): Observable<ClaimsState> {
    return this._store
      .dispatch(new actions.DeleteClaimFile(fileId))
      .pipe(map(() => this._store.snapshot()));
  }

  getClaimFileStatus(fileId: string): Observable<ClaimsState> {
    return this._store
      .dispatch(new actions.GetClaimFileStatus(fileId))
      .pipe(map(() => this._store.snapshot()));
  }

  reverseClaim(claimUploadId: string): Observable<ClaimsState> {
    return this._store
      .dispatch(new actions.ReverseClaimUpload(claimUploadId))
      .pipe(map(() => this._store.snapshot()));
  }

  clearClaimsGroups(): Observable<ClaimsState> {
    return this._store
      .dispatch(new actions.ClearClaimsGroupsList())
      .pipe(map(() => this._store.snapshot()));
  }

  getClaimsGroupsV2(
    params: ClaimsGroupListParamsV2
  ): Observable<{ Claims: IClaimsState }> {
    return this._store
      .dispatch(new actions.GetClaimsManagementDataV2(params, false))
      .pipe(map(() => this._store.snapshot()));
  }

  getClaimsGroupClaimsV2(
    params: ClaimsGroupListParamsV2
  ): Observable<{ Claims: IClaimsState }> {
    return this._store
      .dispatch(new actions.GetClaimsManagementDataV2(params, true))
      .pipe(map(() => this._store.snapshot()));
  }

  clearClaims(): Observable<ClaimsState> {
    return this._store
      .dispatch(new actions.ClearClaimsList())
      .pipe(map(() => this._store.snapshot()));
  }

  getClaims(
    userId: string,
    fileId: string,
    skip: number,
    take: number,
    entityId?: string,
    statusCode?: string,
    sort?: SortDescriptor[],
    serviceType?: CustomerServiceType
  ): Observable<ClaimsState> {
    return this._store
      .dispatch(
        new actions.GetClaimsList(
          userId,
          fileId,
          skip,
          take,
          entityId,
          statusCode,
          sort,
          serviceType
        )
      )
      .pipe(map(() => this._store.snapshot()));
  }

  clearClaimsCustomers(): Observable<ClaimsState> {
    return this._store
      .dispatch(new actions.ClearClaimsCustomersList())
      .pipe(map(() => this._store.snapshot()));
  }

  getClaimsCustomers(
    skip = 0,
    take = 10,
    user?: ModelUser,
    sort?: SortDescriptor[],
    filter?: CompositeFilterDescriptor
  ): Observable<ClaimsState> {
    return this._store
      .dispatch(
        new actions.GetClaimsCustomersList(skip, take, user, sort, filter)
      )
      .pipe(map(() => this._store.snapshot()));
  }

  clearClaimDetails(): Observable<ClaimsState> {
    return this._store
      .dispatch(new actions.ClearClaimDetails())
      .pipe(map(() => this._store.snapshot()));
  }

  getClaimDetails(
    userId: string,
    claimIntakeId: string
  ): Observable<ClaimsState> {
    return this._store
      .dispatch(new actions.GetClaimDetails(userId, claimIntakeId))
      .pipe(map(() => this._store.snapshot()));
  }

  updateClaim(
    params: ClaimBatchUpdateParamsV2
  ): Observable<ClaimsState | null> {
    return this._store.dispatch(new actions.UpdateClaim(params)).pipe(
      switchMap(() => {
        if (params.userId && params.intakeIds?.length) {
          return this.getClaimDetails(params.userId!, params.intakeIds[0]);
        }
        return of(null);
      }),
      tap(() =>
        this.claimUpdated$.next(
          params.intakeIds?.length ? params.intakeIds[0] : undefined
        )
      )
    );
  }

  update340BAuditSingleClaim(
    params: IClaimAuditStatusUpdateParams
  ): Observable<{ Claims: IClaimsState }> {
    return this._store
      .dispatch(new actions.Update340BAuditSingleClaim(params))
      .pipe(
        map(() => this._store.snapshot()),
        tap(() => this.claimUpdated$.next(params.intakeId))
      );
  }

  complete340BAuditManualReviewSingleClaim(
    intakeId: string
  ): Observable<{ Claims: IClaimsState }> {
    return this._store
      .dispatch(new actions.Complete340BAuditManualReviewSingleClaim(intakeId))
      .pipe(
        map(() => this._store.snapshot()),
        tap(() => this.claimUpdated$.next(intakeId))
      );
  }

  getClaimsSummary(params: ClaimsSummaryParams): Observable<ClaimsState> {
    return this._store
      .dispatch(new actions.GetClaimsSummary(params))
      .pipe(map(() => this._store.snapshot()));
  }

  getAuditClaimFiles(
    params: AuditClaimFileSummaryParams
  ): Observable<{ Claims: IClaimsState }> {
    return this._store
      .dispatch(new actions.GetAuditClaimFiles(params))
      .pipe(map(() => this._store.snapshot()));
  }

  getReviewedAuditClaimFiles(
    customerId: string
  ): Observable<{ Claims: IClaimsState }> {
    return this._store
      .dispatch(new actions.GetReviewedAuditClaimFiles(customerId))
      .pipe(map(() => this._store.snapshot()));
  }

  getAuditClaimTotals(
    customerId: string,
    filters?: { [key: string]: string | number }
  ): Observable<{ Claims: IClaimsState }> {
    return this._store
      .dispatch(new actions.GetAuditClaimTotals(customerId, filters))
      .pipe(map(() => this._store.snapshot()));
  }

  getAuditReportingStatusTotals(
    customerId: string,
    filters?: { [key: string]: string | number }
  ): Observable<{ Claims: IClaimsState }> {
    return this._store
      .dispatch(new actions.GetAuditReportingStatusTotals(customerId, filters))
      .pipe(map(() => this._store.snapshot()));
  }

  getAuditReportingResolutionTrends(
    customerId: string,
    filters?: { [key: string]: string | number }
  ): Observable<{ Claims: IClaimsState }> {
    return this._store
      .dispatch(
        new actions.GetAuditReportingResolutionTrends(customerId, filters)
      )
      .pipe(map(() => this._store.snapshot()));
  }

  getAuditReportingTop340B(
    customerId: string,
    filters?: { [key: string]: string | number }
  ): Observable<{ Claims: IClaimsState }> {
    return this._store
      .dispatch(new actions.GetAuditReportingTop340B(customerId, filters))
      .pipe(map(() => this._store.snapshot()));
  }

  getAuditReportingTopNot340B(
    customerId: string,
    filters?: { [key: string]: string | number }
  ): Observable<{ Claims: IClaimsState }> {
    return this._store
      .dispatch(new actions.GetAuditReportingTopNot340B(customerId, filters))
      .pipe(map(() => this._store.snapshot()));
  }

  getAuditReportingTopDisputed(
    customerId: string,
    filters?: { [key: string]: string | number }
  ): Observable<{ Claims: IClaimsState }> {
    return this._store
      .dispatch(new actions.GetAuditReportingTopDisputed(customerId, filters))
      .pipe(map(() => this._store.snapshot()));
  }

  getAuditReportingTopDisputeResolution(
    customerId: string,
    filters?: { [key: string]: string | number }
  ): Observable<{ Claims: IClaimsState }> {
    return this._store
      .dispatch(
        new actions.GetAuditReportingTopDisputeResolution(customerId, filters)
      )
      .pipe(map(() => this._store.snapshot()));
  }

  clearAuditReporting(): Observable<{ Claims: IClaimsState }> {
    return this._store
      .dispatch(new actions.ClearAuditReporting())
      .pipe(map(() => this._store.snapshot()));
  }

  getClaimsSummaryBatchStatistics(
    params: ClaimsSummaryStatisticsParams
  ): Observable<ClaimsState> {
    return this._store
      .dispatch(new actions.GetClaimsSummaryBatchStatistics(params))
      .pipe(map(() => this._store.snapshot()));
  }

  clearClaimsSummaryBatchStatistics(
    level?: BatchStatisticsLevel
  ): Observable<ClaimsState> {
    return this._store
      .dispatch(new actions.ClearClaimsSummaryBatchStatistics(level))
      .pipe(map(() => this._store.snapshot()));
  }

  get340BAuditClaimsCEs(
    params: ClaimsCEsGetParams
  ): Observable<{ Claims: IClaimsState }> {
    return this._store
      .dispatch(new claimCEActions.Get340BAuditClaimsCEs(params))
      .pipe(map(() => this._store.snapshot()));
  }

  clear340BAuditClaimsCEs(): Observable<{ Claims: IClaimsState }> {
    return this._store
      .dispatch(new claimCEActions.Clear340BAuditClaimsCEs())
      .pipe(map(() => this._store.snapshot()));
  }

  contact340BAuditClaimsCEs(
    params: ClaimsCEsContactParams
  ): Observable<{ Claims: IClaimsState }> {
    return this._store
      .dispatch(new claimCEActions.Contact340BAuditClaimsCEs(params))
      .pipe(map(() => this._store.snapshot()));
  }

  clearMFPClaims(): Observable<{ Claims: IClaimsState }> {
    return this._store
      .dispatch(new mfpClaimsActions.ClearMFPClaims())
      .pipe(map(() => this._store.snapshot()));
  }

  getMFPClaims(
    params: MFPClaimsGetParams
  ): Observable<{ Claims: IClaimsState }> {
    return this._store
      .dispatch(new mfpClaimsActions.GetMFPClaims(params))
      .pipe(map(() => this._store.snapshot()));
  }

  updateMFPClaims(
    params: MFPClaimsUpdateParams
  ): Observable<{ Claims: IClaimsState }> {
    return this._store
      .dispatch(new mfpClaimsActions.UpdateMFPClaims(params))
      .pipe(map(() => this._store.snapshot()));
  }
}
